import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

function toDate(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed : null;
}

/** "12 Mar 2025" — stable, unambiguous, locale-independent. */
export function formatDate(iso: string | undefined | null): string {
  const date = toDate(iso);
  return date ? format(date, 'd MMM yyyy') : '—';
}

/** "12 Mar 2025, 14:05" */
export function formatDateTime(iso: string | undefined | null): string {
  const date = toDate(iso);
  return date ? format(date, 'd MMM yyyy, HH:mm') : '—';
}

/** "3 days ago" */
export function formatRelative(iso: string | undefined | null): string {
  const date = toDate(iso);
  return date ? `${formatDistanceToNowStrict(date)} ago` : '—';
}

/** Full precision for a `title` attribute / screen-reader detail. */
export function formatAbsoluteTitle(iso: string | undefined | null): string {
  const date = toDate(iso);
  return date ? date.toISOString() : '';
}

/** Best available human name for the person who FIRST shared a link. */
export function firstSharerName(share: {
  slackUserDisplayName?: string;
  slackUserRealName?: string;
  slackUserEmail?: string;
  slackUserId: string;
}): string {
  return (
    share.slackUserDisplayName ??
    share.slackUserRealName ??
    share.slackUserEmail ??
    share.slackUserId
  );
}

/**
 * Channel name with a leading #, falling back to the raw Slack channel id. A direct message (IM id
 * starts with 'D', mirroring isDirectMessageChannelId in @slc/shared) is labelled "Direct message"
 * since it has no channel name, and a link added on the dashboard "Dashboard" since it has no
 * conversation at all — the same labels as the channel filter's buckets.
 */
export function channelLabel(share: {
  source?: string;
  slackChannelName?: string;
  slackChannelId: string;
}): string {
  if (share.source === 'dashboard') return 'Dashboard';
  if (share.slackChannelId.startsWith('D')) return 'Direct message';
  return share.slackChannelName ? `#${share.slackChannelName}` : share.slackChannelId;
}

/** Trim a URL down to something that fits in a card without wrapping forever. */
export function truncate(value: string, max = 72): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

/**
 * `142 of 1200` -> `"12%"`. Tiny shares round to "<1%" rather than a misleading "0%"; a zero
 * count or total yields an empty string so callers can simply not render anything.
 */
export function formatShare(count: number, total: number): string {
  if (total <= 0 || count <= 0) return '';
  const pct = (count / total) * 100;
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
}

/** `2048` -> `"2 KB"`, `5_400_000` -> `"5.1 MB"`. Binary units, the way file managers show them. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below 10, dropped when it is zero: "1.5 KB", "2 KB", "5.1 MB", "12 MB".
  return `${value < 10 ? Number(value.toFixed(1)) : Math.round(value)} ${units[unit]}`;
}

/** A video's length the way players show it: `"0:07"`, `"4:05"`, `"1:02:03"`. */
export function formatDuration(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '—';
  const totalSeconds = Math.round(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

/**
 * Two-letter monogram for an avatar placeholder. `@`, `.`, `_` and `-` all count as word
 * separators so an email address ("amelie@seez.tech") yields a sensible "AS".
 */
export function initials(name: string): string {
  const parts = name
    .replace(/[^\p{L}\p{N}\s.@_-]/gu, ' ')
    .split(/[\s._@-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
}
